/**
 * Saved Filters Actions
 * Handles loading, saving, updating, and deleting user filter configurations
 */

import { firecollection } from '@/store/firebase.js'

export default {
  /**
   * Load all saved filters for the current user
   * Fetches from both person document and settings collection
   */
  async loadFilters({ commit, rootState }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const userEmail = rootState.auth.user?.email

      if (!userEmail) {
        throw new Error('User not authenticated')
      }

      // Load from person document
      const personDoc = await firecollection.get('person', userEmail)
      const personFilters = personDoc?.settings?.filters || []

      // Load from settings collection (fallback/redundancy)
      const settingsDoc = await firecollection.get('setting', userEmail)
      const settingsFilters = settingsDoc?.filters || []

      // Merge both sources, preferring person doc
      const filtersMap = new Map()

      // Add settings collection filters first
      settingsFilters.forEach((filter) => {
        if (filter.id) {
          filtersMap.set(filter.id, filter)
        }
      })

      // Override with person doc filters (these take precedence)
      personFilters.forEach((filter) => {
        if (filter.id) {
          filtersMap.set(filter.id, filter)
        }
      })

      const allFilters = Array.from(filtersMap.values())

      commit('SET_FILTERS', allFilters)
    } catch (error) {
      // Error already logged to Sentry via firecollection.error
      commit('SET_ERROR', error.message)
      commit('SET_FILTERS', [])
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * Save a new filter
   * @param {Object} filterData - Filter configuration
   * @param {String} filterData.name - Display name for the filter
   * @param {String} filterData.view - View name (e.g., 'time', 'pacing')
   * @param {Object} filterData.filters - Filter values
   */
  async saveFilter({ commit, dispatch, rootState }, filterData) {
    try {
      const userEmail = rootState.auth.user?.email

      if (!userEmail) {
        throw new Error('User not authenticated')
      }

      // Create filter object
      const newFilter = {
        id: `filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: filterData.name,
        view: filterData.view,
        filters: filterData.filters,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }

      // Add to state
      commit('ADD_FILTER', newFilter)

      // Persist to Firestore (both locations)
      await dispatch('persistFilters', userEmail)

      // Show success message
      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', {
        message: `Filter "${newFilter.name}" saved successfully`,
        status: 'success',
      }, { root: true })

      return newFilter
    } catch (error) {
      // Error already logged to Sentry
      commit('SET_ERROR', error.message)

      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', {
        message: `Error saving filter: ${error.message}`,
        status: 'error',
      }, { root: true })

      throw error
    }
  },

  /**
   * Update an existing filter
   * @param {String} id - Filter ID
   * @param {Object} updates - Properties to update
   */
  async updateFilter({ commit, dispatch, rootState }, { id, updates }) {
    try {
      const userEmail = rootState.auth.user?.email

      if (!userEmail) {
        throw new Error('User not authenticated')
      }

      // Update in state
      commit('UPDATE_FILTER', {
        id,
        updates: {
          ...updates,
          updatedAt: new Date().toISOString(),
        },
      })

      // Persist to Firestore
      await dispatch('persistFilters', userEmail)

      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', {
        message: 'Filter updated successfully',
        status: 'success',
      }, { root: true })
    } catch (error) {
      // Error already logged to Sentry
      commit('SET_ERROR', error.message)

      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', {
        message: `Error updating filter: ${error.message}`,
        status: 'error',
      }, { root: true })

      throw error
    }
  },

  /**
   * Delete a filter
   * @param {String} id - Filter ID to delete
   */
  async deleteFilter({ commit, dispatch, rootState }, id) {
    try {
      const userEmail = rootState.auth.user?.email

      if (!userEmail) {
        throw new Error('User not authenticated')
      }

      // Remove from state
      commit('DELETE_FILTER', id)

      // Persist to Firestore
      await dispatch('persistFilters', userEmail)

      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', {
        message: 'Filter deleted successfully',
        status: 'success',
      }, { root: true })
    } catch (error) {
      // Error already logged to Sentry
      commit('SET_ERROR', error.message)

      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', {
        message: `Error deleting filter: ${error.message}`,
        status: 'error',
      }, { root: true })

      throw error
    }
  },

  /**
   * Set the currently active filter
   * @param {String} id - Filter ID to set as active
   */
  setActiveFilter({ commit }, id) {
    commit('SET_ACTIVE_FILTER', id)
  },

  /**
   * Clear the active filter
   */
  clearActiveFilter({ commit }) {
    commit('SET_ACTIVE_FILTER', null)
  },

  /**
// [MermaidChart: d707a56f-2dc1-44ef-90a9-5b89b6c1a759]
   * Persist all filters to Firestore (both person doc and settings collection)
   * @param {String} userEmail - User's email address
   */
  async persistFilters({ state }, userEmail) {
    const { filters } = state

    // Save to person document under settings.filters
    const personDoc = await firecollection.get('person', userEmail)
    const updatedSettings = {
      ...personDoc?.settings,
      filters,
    }

    await firecollection.set('person', userEmail, {
      settings: updatedSettings,
    }, { merge: true })

    // Also save to settings collection (redundancy)
    await firecollection.set('setting', userEmail, {
      filters,
      updatedAt: new Date().toISOString(),
    }, { merge: true })
  },
}
